import { Avatar, Fab } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { format } from "date-fns";
import { useDispatch } from "react-redux";

import { ELEMENT_COLOR_VALUE, ELEMENT_POSITIONING, ELEMENT_TEXT_COLOR_VALUE, ELEMENT_TYPE } from "@/constants";
import { DeleteWhite, EditWhite } from "@/icons";
import { setSimpleValue, updateVisualBuilderData } from "@/store/actions";
import { setSlideToIndex } from "@/store/v2/swiper/actions";
import { ELEMENT_DEFAULT_ICONS_VALUE, ELEMENT_ICONS_VALUE } from "@/utils/element";
import { getMediaLink } from "@/utils/mediaLink";

import DeleteElement from "../../DeleteElement/DeleteElement";
import AudioPlayback from "../AudioPlayback/AudioPlayback";

const useStyles = makeStyles({
  elementContainer: {
    display: "flex",
    flexDirection: "column",
    padding: "16px",
    gap: "16px",
    backgroundColor: "#000",
    justifyContent: "center",
    border: "0px",
  },
  elementContent: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    gap: "16px",
    borderWidth: 0,
  },
  icons: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  iconContainer: {
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
  },
  icon: {
    maxWidth: "24px",
    maxHeight: "24px",
  },
  details: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    alignItems: "space-between",
    height: "80px",
  },
  detailsItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    height: "32px",
  },
  title: {
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 700,
    color: "#fff",
  },
  mediaName: {
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 400,
    color: "#4182E1",
  },
  creatorInfo: {
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "16px",
    color: "#757575",
  },
  actions: {
    display: "flex",
    gap: "16px",
    flexDirection: "column",
    height: "80px",
    justifyContent: "space-around",
  },
  fabButton: {
    width: 28,
    height: 28,
    minHeight: 28,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#161616",
    color: "#fff",
    borderRadius: "50%",
    fontWeight: 500,
    fontSize: "12px",
    "&:hover": {
      backgroundColor: "#F50561",
    },
    "& svg": {
      width: 14,
      height: 14,
    },
  },
});

export default function VoiceMemoItem(props) {
  const { params, element = {}, onUpdate, onRemove, selectedElementId } = props;
  const { createdBy } = element;

  const classes = useStyles({});

  const dispatch = useDispatch();

  const handleEdit = () => {
    if (element.mediaItemId) {
      dispatch(
        updateVisualBuilderData(
          params,
          {
            currentMediaItemId: element.mediaItemId,
            typeFilterId: null,
            tagFilterId: null,
          },
          {},
          true,
        ),
      );

      dispatch(setSlideToIndex(element.mediaItemId));
    }

    onUpdate(element);
  };

  const getIconPath = () =>
    ELEMENT_DEFAULT_ICONS_VALUE.includes(element.iconLink)
      ? `/icons/v2/image_${element.textColor}/${element.iconName}.svg`
      : getMediaLink({ link: element.iconLink });

  const iconProps = {
    htmlColor: ELEMENT_TEXT_COLOR_VALUE[element.textColor] || "#fff",
  };

  const IconComponent = ELEMENT_ICONS_VALUE[element.type] || ELEMENT_ICONS_VALUE[ELEMENT_TYPE.LINK];

  return (
    <button
      type="button"
      tabIndex={0}
      key={element.id}
      id={`element-${element.id}`}
      className={classes.elementContainer}
      data-testid="element-container"
    >
      <div className={classes.elementContent}>
        <div className={classes.icons}>
          <div
            className={classes.iconContainer}
            style={{ backgroundColor: ELEMENT_COLOR_VALUE[element.color] || ELEMENT_COLOR_VALUE.DEFAULT }}
          >
            {element.iconLink ? (
              <img src={getIconPath()} alt={element.iconName || "Element"} className={classes.icon} />
            ) : (
              <span style={{ lineHeight: "0", display: "inline-block" }}>
                <IconComponent className={classes.icon} {...iconProps} />
              </span>
            )}
          </div>
          <Avatar
            src={getMediaLink({ link: createdBy?.photoUrl })}
            alt={createdBy?.name}
            style={{ width: "32px", height: "32px" }}
          />
        </div>
        <div className={classes.details}>
          <div className={classes.detailsItem}>
            <div className={classes.title}>{element.title}</div>
            <div className={classes.mediaName}>
              {element.positioning === ELEMENT_POSITIONING.EXPERIENCE ? "Entire Experience" : element.mediaItem?.name}
            </div>
          </div>
          <div className={classes.detailsItem}>
            <span className={classes.creatorInfo}>{createdBy?.name}</span>
            <span className={classes.creatorInfo}>{format(new Date(element.createdAt), "dd MMM yyyy")}</span>
          </div>
        </div>
        <div className={classes.actions}>
          <Fab
            className={classes.fabButton}
            size="small"
            onClick={() => handleEdit()}
            data-analytics={`${element.type}-view-edit`}
          >
            <EditWhite htmlColor="#fff" />
          </Fab>
          <Fab
            className={classes.fabButton}
            size="small"
            onClick={() => dispatch(setSimpleValue("selectedElementId", element.id))}
            data-analytics={`${element.type}-view-delete`}
          >
            <DeleteWhite htmlColor="#fff" />
          </Fab>
        </div>
      </div>
      <AudioPlayback url={getMediaLink({ link: element.url })} />
      {selectedElementId === element.id && (
        <DeleteElement element={element} onRemove={onRemove} selectedElementId={selectedElementId} />
      )}
    </button>
  );
}
